import { ATTRIBUTION_TAB_HIERARCHY, CONVERSION_VALUE_SOURCES } from '@/features/attribution/constants/attribution-constants';
import type {
  AttributionRowData,
  DimensionFilter,
  DimensionFilterGroup,
} from '@/features/attribution/types/attribution-types';
import { normalizeAttributionDimension } from '@/features/attribution/helpers/attribution-helpers';
import { getCustomLabelFromRow, getFilterValueFromRow } from '@/features/attribution/helpers/attribution-row-keys';

import { DIMENSION } from '@adtrackify/at-tracking-event-types';

import { ensureViewExists } from './ensure-view-exists';
import type { AttributionActions, ImmerSet } from '../../types/filter-actions-types';

// ============================================================================
// Action Creator
// ============================================================================

export function createAttributionActions(set: ImmerSet): AttributionActions {
  return {
    setDimension: (viewId, dimension) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        // Legacy ADSET/AD → TERM/CONTENT so keep/clear matches live tabs (C1).
        const normalizedDimension = normalizeAttributionDimension(dimension);
        const currentDimension =
          view.dimension === undefined
            ? undefined
            : normalizeAttributionDimension(view.dimension);

        // If dimension is not set yet, just set it
        if (currentDimension === undefined) {
          view.dimension = normalizedDimension;
          return;
        }

        if (normalizedDimension === currentDimension) return;

        // Use tab hierarchy (Source→Campaign→Term→Content) for up/down navigation.
        const currentDimensionIndex = ATTRIBUTION_TAB_HIERARCHY.indexOf(currentDimension);
        const newDimensionIndex = ATTRIBUTION_TAB_HIERARCHY.indexOf(normalizedDimension);

        // Non-tab dimensions (e.g. Medium): set without keep/clear side effects
        if (currentDimensionIndex === -1 || newDimensionIndex === -1) {
          view.dimension = normalizedDimension;
          return;
        }

        // When moving up the hierarchy, clear lower dimensions and rebuild filters
        if (newDimensionIndex < currentDimensionIndex) {
          const dimensionsToKeep = ATTRIBUTION_TAB_HIERARCHY.slice(0, newDimensionIndex);
          const selectedRowsByDimension = view.selectedRowsByDimension ?? {};

          // Build filters from selections for dimensions we're keeping
          const filters: DimensionFilterGroup[] = [];
          for (const dim of dimensionsToKeep) {
            const dimData = selectedRowsByDimension[dim];
            if (dimData) {
              const filterGroup = buildFiltersFromDimensionData(dimData, dim);
              if (filterGroup.length > 0) {
                filters.push(filterGroup);
              }
            }
          }

          // Clear tab dims below target, plus legacy ADSET/AD aliases
          const dimensionsToClear = [
            ...ATTRIBUTION_TAB_HIERARCHY.slice(newDimensionIndex + 1),
            DIMENSION.UTM_ADSET,
            DIMENSION.UTM_AD,
          ];
          const newSelectedRows = { ...selectedRowsByDimension };
          for (const dim of dimensionsToClear) {
            delete newSelectedRows[dim];
          }

          view.dimension = normalizedDimension;
          view.dimensionFilters = filters.length > 0 ? filters : null;
          view.selectedRowsByDimension = newSelectedRows;
        } else {
          view.dimension = normalizedDimension;
        }
      });
    },

    setDimensionFilters: (viewId, filters) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.dimensionFilters = filters;
      });
    },

    addDimensionFilter: (viewId, filter) => {
      set(state => {
        const view = ensureViewExists(state, viewId);

        const currentFilters = view.dimensionFilters ?? [];
        const existingGroupIndex = currentFilters.findIndex(
          group => group.length > 0 && group[0].dimension === filter.dimension
        );

        if (existingGroupIndex >= 0) {
          const existingFilterIndex = currentFilters[existingGroupIndex].findIndex(
            f => f.dimension === filter.dimension && f.value === filter.value
          );

          if (existingFilterIndex >= 0) {
            currentFilters[existingGroupIndex][existingFilterIndex] = filter;
          } else {
            currentFilters[existingGroupIndex].push(filter);
          }
        } else {
          currentFilters.push([filter]);
        }

        view.dimensionFilters = currentFilters;
      });
    },

    removeDimensionFilter: (viewId, dimension) => {
      set(state => {
        const view = ensureViewExists(state, viewId);

        const current = view.dimensionFilters;
        if (!current) return;

        const newFilters = current.filter(
          group => group.length === 0 || group[0].dimension !== dimension
        );

        view.dimensionFilters = newFilters.length > 0 ? newFilters : null;
      });
    },

    clearDimensionFilters: (viewId) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.dimensionFilters = null;
      });
    },

    updateDimensionAndFilters: (viewId, dimension, filters) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.dimension = normalizeAttributionDimension(dimension);
        view.dimensionFilters = filters.length > 0 ? filters : null;
      });
    },

    setAttributionEvent: (viewId, event) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.attributionEvent = event;
      });
    },

    setValueSource: (viewId, source) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.valueSource = source;

        // Reset to purchase when switching to orders
        if (source === CONVERSION_VALUE_SOURCES.ORDERS) {
          view.conversionEvent = 'purchase';
        }
      });
    },

    setConversionEvent: (viewId, event) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.conversionEvent = event;
      });
    },

    hydrateAttributionFromUrl: (viewId, params) => {
      set(state => {
        const view = ensureViewExists(state, viewId);

        if (params.dimension !== undefined) {
          view.dimension = normalizeAttributionDimension(params.dimension);
        }

        if (params.dimensionFilters !== undefined) {
          view.dimensionFilters =
            params.dimensionFilters && params.dimensionFilters.length > 0
              ? params.dimensionFilters
              : null;
        }

        if (params.attributionEvent !== undefined) {
          view.attributionEvent = params.attributionEvent;
        }

        if (params.valueSource !== undefined) {
          view.valueSource = params.valueSource;
        } else if (params.isShopifyAttribution !== undefined) {
          // Backward compatibility
          view.valueSource = params.isShopifyAttribution
            ? CONVERSION_VALUE_SOURCES.ORDERS
            : CONVERSION_VALUE_SOURCES.EVENTS;
        }

        if (params.conversionEvent !== undefined) {
          view.conversionEvent = params.conversionEvent;
        }
      });
    },
  };
}

// ============================================================================
// Helpers
// ============================================================================

interface DimensionData {
  rows: Record<string, boolean>;
  data: Record<string, AttributionRowData>;
}

function buildFiltersFromDimensionData(
  dimData: DimensionData,
  dimension: DIMENSION
): DimensionFilter[] {
  const { rows, data } = dimData;

  const filters: DimensionFilter[] = [];
  for (const [rowId, selected] of Object.entries(rows)) {
    if (!selected) continue;

    const rowData = data[rowId];
    if (!rowData) continue;

    const filterValue = getFilterValueFromRow(rowData, dimension);
    if (!filterValue) continue;

    filters.push({
      dimension,
      value: filterValue,
      rowId,
      customLabel: getCustomLabelFromRow(rowData, dimension),
    });
  }
  return filters;
}
